import React, { memo } from 'react';

import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import FileList from './file-upload/FileList';
import useLocales from '../../hooks/useLocales';
import Scrollbar from '../../components/Scrollbar';
import FileUploadWidget from './file-upload/FileUploadWidget';
import { DocumentModuleTypes } from '../../@types/shared/enums/documentModuleTypes';

// --------------------------------------------------------

interface FileUploadSectionsProps {
  isDocument: boolean;
  formModuleType: DocumentModuleTypes | undefined;
  mainFile: File[];
  setMainFile: React.Dispatch<React.SetStateAction<File[]>>;
  attachments: File[];
  setAttachments: React.Dispatch<React.SetStateAction<File[]>>;
  accompanyingDocuments: File[];
  setAccompanyingDocuments: React.Dispatch<React.SetStateAction<File[]>>;
  onCombinedModeClick: () => void;
  mainFileError?: boolean;
}

/**
 * Component containing all file upload sections (Main, Attachments, Accompanying)
 * Memoized to prevent unnecessary re-renders
 */
const FileUploadSections: React.FC<FileUploadSectionsProps> = ({
  isDocument,
  formModuleType,
  mainFile,
  setMainFile,
  attachments,
  setAttachments,
  accompanyingDocuments,
  setAccompanyingDocuments,
  onCombinedModeClick,
  mainFileError = false,
}) => {
  const { t } = useLocales();

  return (
    <>
      {/* Main Document */}
      {formModuleType === DocumentModuleTypes.Accounting && (
        <Grid size={{ xs: 12 }}>
          <Accordion defaultExpanded sx={{ p: 3 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 3 }}>
              <Typography variant="h6"> {t('documents.mainDocument')}</Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 3, pt: 0 }}>
              {!isDocument && mainFile.length === 0 && (
                <Stack spacing={0.5}>
                  <FileUploadWidget
                    multiple={false}
                    acceptableFileFormats=".pdf"
                    maxFileSize={50 * 1024 * 1024}
                    setFiles={setMainFile}
                    error={mainFileError}
                  />

                  {mainFileError && (
                    <Typography variant="caption" color="error" sx={{ pl: 0.5 }}>
                      {t('forms.validationErrorMessage.mainDocumentRequired')}
                    </Typography>
                  )}
                </Stack>
              )}

              {mainFile.length > 0 && (
                <Stack spacing={2}>
                  <FileList files={mainFile} setFiles={setMainFile} />

                  <Button variant="contained" onClick={onCombinedModeClick}>
                    {t('documents.mergedDocuments')}
                  </Button>
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}

      {/* Attachments */}
      <Grid size={{ xs: 12 }}>
        <Accordion defaultExpanded={attachments.length > 0} sx={{ p: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 3 }}>
            <Typography variant="h6"> {t('documents.attachments')}</Typography>
          </AccordionSummary>

          <AccordionDetails sx={{ p: 3, pt: 0 }}>
            <Stack spacing={2}>
              {attachments.length > 0 && (
                <Scrollbar sx={{ maxHeight: '230px' }}>
                  <FileList files={attachments} setFiles={setAttachments} />
                </Scrollbar>
              )}

              <FileUploadWidget
                multiple
                acceptableFileFormats=".pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .jpeg, .jpg, .png, .txt"
                maxFileSize={50 * 1024 * 1024}
                setFiles={setAttachments}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Grid>

      {/* Accompanying Documents */}
      <Grid size={{ xs: 12 }}>
        <Accordion defaultExpanded={accompanyingDocuments.length > 0} sx={{ p: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 3 }}>
            <Typography variant="h6"> {t('documents.accompanyingDocuments')}</Typography>
          </AccordionSummary>

          <AccordionDetails sx={{ p: 3, pt: 0 }}>
            <Stack spacing={2}>
              {accompanyingDocuments.length > 0 && (
                <Scrollbar sx={{ maxHeight: '230px' }}>
                  <FileList files={accompanyingDocuments} setFiles={setAccompanyingDocuments} />
                </Scrollbar>
              )}

              <FileUploadWidget
                multiple
                acceptableFileFormats=".pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .jpeg, .jpg, .png, .adoc, .edoc, .bdoc, .asice, .txt"
                maxFileSize={50 * 1024 * 1024}
                setFiles={setAccompanyingDocuments}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Grid>
    </>
  );
};

export default memo(FileUploadSections);
